import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Gift, Heart, ArrowLeft, Check, Copy } from "lucide-react";

const AMOUNTS = [10, 20, 30, 50, 75, 100];

export default function BuyGiftVoucher() {
  const [step, setStep] = useState(1); // 1=amount, 2=details, 3=done
  const [amount, setAmount] = useState(50);
  const [customAmount, setCustomAmount] = useState('');
  const [fromName, setFromName] = useState('');
  const [fromEmail, setFromEmail] = useState('');
  const [toName, setToName] = useState('');
  const [toEmail, setToEmail] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [voucher, setVoucher] = useState(null);
  const [settings, setSettings] = useState({});

  useEffect(() => {
    axios.get(`${API}/settings?lite=true`).then(r => setSettings(r.data || {})).catch(() => {});
  }, []);

  const salonName = settings.salon_name || '';
  const salonLogo = settings.icon_logo_url || settings.logo_url || '';

  const effectiveAmount = customAmount ? parseFloat(customAmount) : amount;

  const handlePurchase = async () => {
    if (!fromName || !fromEmail) { toast.error('Please enter your name and email'); return; }
    if (!toName || !toEmail) { toast.error('Please enter recipient name and email'); return; }
    if (effectiveAmount < 5) { toast.error('Minimum voucher value is £5'); return; }
    
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/vouchers`, {
        type: 'GIFT_CARD',
        initial_value: effectiveAmount,
        purchased_by_name: fromName,
        purchased_by_email: fromEmail,
        recipient_name: toName,
        recipient_email: toEmail,
        recipient_message: message,
        issued_reason: 'Gift voucher purchase'
      });
      setVoucher(res.data);
      setStep(3);
      toast.success('Gift voucher created!');
    } catch (e) {
      toast.error('Failed to create voucher');
    }
    setSubmitting(false);
  };

  const copyCode = () => {
    if (voucher?.code) {
      navigator.clipboard.writeText(voucher.code);
      toast.success('Code copied!');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Toaster position="top-center" />
      
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-sm border-b border-border">
        <div className="max-w-3xl mx-auto px-4 flex items-center justify-between h-14">
          <Link to="/" className="flex items-center gap-2">
            {salonLogo ? (
              <img src={salonLogo} alt={salonName} className="w-8 h-8 rounded-lg object-cover" />
            ) : (
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary font-bold text-xs">
                {((salonName || 'B')[0]).toUpperCase()}
              </div>
            )}
            <span className="font-semibold text-sm" style={{ fontFamily: 'Playfair Display' }}>{salonName}</span>
          </Link>
          <Link to="/"><Button variant="ghost" size="sm" className="rounded-xl text-xs"><ArrowLeft size={14} className="mr-1" /> Back</Button></Link>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Step 1: Choose amount */}
        {step === 1 && (
          <div className="fade-in">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-gradient-to-br from-primary to-primary/60 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Gift size={32} className="text-white" />
              </div>
              <h1 className="text-3xl font-semibold tracking-tight mb-2" style={{ fontFamily: 'Playfair Display' }}>
                Gift Voucher
              </h1>
              <p className="text-muted-foreground">The perfect gift for someone who deserves beautiful nails</p>
            </div>

            <Card className="p-6 rounded-2xl border shadow-[0_10px_30px_rgba(43,18,27,0.06)]">
              <h2 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Playfair Display' }}>Choose an amount</h2>
              <div className="grid grid-cols-3 gap-3 mb-4">
                {AMOUNTS.map(a => (
                  <button
                    key={a}
                    onClick={() => { setAmount(a); setCustomAmount(''); }}
                    className={`p-4 rounded-xl text-center border-2 transition-all duration-150 ${
                      amount === a && !customAmount
                        ? 'border-primary bg-primary/5 shadow-md'
                        : 'border-border hover:border-primary/30'
                    }`}
                  >
                    <p className="text-2xl font-semibold" style={{ fontFamily: 'Playfair Display' }}>£{a}</p>
                  </button>
                ))}
              </div>
              <div>
                <Label className="text-sm">Or enter a custom amount</Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground font-medium">£</span>
                  <Input
                    type="number"
                    value={customAmount}
                    onChange={e => { setCustomAmount(e.target.value); }}
                    placeholder="Enter amount"
                    className="pl-8 rounded-xl text-lg"
                    min={5}
                  />
                </div>
              </div>
              <div className="mt-6 text-center">
                <p className="text-3xl font-semibold text-primary mb-4" style={{ fontFamily: 'Playfair Display' }}>
                  £{effectiveAmount.toFixed(2)}
                </p>
                <Button onClick={() => setStep(2)} disabled={effectiveAmount < 5} className="rounded-xl px-8 h-12 text-base">
                  Continue <Heart size={16} className="ml-2" />
                </Button>
              </div>
            </Card>
          </div>
        )}

        {/* Step 2: Details */}
        {step === 2 && (
          <div className="fade-in">
            <div className="flex items-center gap-3 mb-6">
              <Button variant="ghost" onClick={() => setStep(1)} className="rounded-xl"><ArrowLeft size={16} /></Button>
              <div>
                <h2 className="text-2xl font-semibold" style={{ fontFamily: 'Playfair Display' }}>Who is it for?</h2>
                <p className="text-sm text-muted-foreground">£{effectiveAmount.toFixed(2)} gift voucher</p>
              </div>
            </div>

            <div className="space-y-4">
              <Card className="p-5 rounded-2xl border">
                <h3 className="text-sm font-semibold mb-3 text-primary uppercase tracking-wider">Your details</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">Your name *</Label><Input value={fromName} onChange={e => setFromName(e.target.value)} className="mt-1 rounded-xl" placeholder="Your name" /></div>
                  <div><Label className="text-xs">Your email *</Label><Input type="email" value={fromEmail} onChange={e => setFromEmail(e.target.value)} className="mt-1 rounded-xl" placeholder="your@email.com" /></div>
                </div>
              </Card>

              <Card className="p-5 rounded-2xl border">
                <h3 className="text-sm font-semibold mb-3 text-primary uppercase tracking-wider">Recipient details</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">Recipient name *</Label><Input value={toName} onChange={e => setToName(e.target.value)} className="mt-1 rounded-xl" placeholder="Their name" /></div>
                  <div><Label className="text-xs">Recipient email *</Label><Input type="email" value={toEmail} onChange={e => setToEmail(e.target.value)} className="mt-1 rounded-xl" placeholder="their@email.com" /></div>
                </div>
                <div className="mt-3">
                  <Label className="text-xs">Gift message (optional)</Label>
                  <Textarea value={message} onChange={e => setMessage(e.target.value)} className="mt-1 rounded-xl" rows={3} placeholder="Happy birthday! Treat yourself to beautiful nails..." />
                </div>
              </Card>

              <Card className="p-5 rounded-2xl border bg-secondary/20">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold" style={{ fontFamily: 'Playfair Display' }}>Gift Voucher</p>
                    <p className="text-xs text-muted-foreground">For {toName || 'recipient'} from {fromName || 'you'}</p>
                  </div>
                  <p className="text-2xl font-semibold text-primary" style={{ fontFamily: 'Playfair Display' }}>£{effectiveAmount.toFixed(2)}</p>
                </div>
              </Card>

              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3 text-xs text-yellow-800">
                <p className="font-medium">Payment</p>
                <p className="mt-0.5">Please transfer £{effectiveAmount.toFixed(2)} to complete the purchase. The voucher will be sent to the recipient immediately.</p>
                <p className="mt-1 font-medium">Bank: Monzo · Name: Elke Kolar · Account: 47145258 · Sort: 04-00-03</p>
              </div>

              <Button onClick={handlePurchase} disabled={submitting} className="w-full rounded-xl h-12 text-base">
                {submitting ? 'Creating voucher...' : `Purchase £${effectiveAmount.toFixed(2)} Gift Voucher`}
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Confirmation */}
        {step === 3 && voucher && (
          <div className="fade-in text-center py-8">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check size={32} className="text-green-600" />
            </div>
            <h2 className="text-2xl font-semibold tracking-tight mb-2" style={{ fontFamily: 'Playfair Display' }}>Voucher Created!</h2>
            <p className="text-muted-foreground mb-6">A beautiful voucher email has been sent to {toEmail}</p>

            <Card className="p-8 rounded-2xl border shadow-[0_10px_30px_rgba(43,18,27,0.08)] max-w-sm mx-auto mb-6" style={{ background: 'linear-gradient(135deg, #fdf2f8 0%, #fff 50%, #fce7f3 100%)' }}>
              {salonLogo ? (
                <img src={salonLogo} alt={salonName} className="w-14 h-14 rounded-xl mx-auto mb-3 object-cover" />
              ) : (
                <div className="w-14 h-14 rounded-xl bg-primary/10 mx-auto mb-3 flex items-center justify-center text-primary text-xl font-bold">
                  {((salonName || 'B')[0]).toUpperCase()}
                </div>
              )}
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">{salonName ? `${salonName} Gift Voucher` : 'Gift Voucher'}</p>
              <p className="text-4xl font-semibold text-primary mb-3" style={{ fontFamily: 'Playfair Display' }}>£{voucher.initial_value?.toFixed(0)}</p>
              <div className="bg-white/80 rounded-xl p-3 mb-3">
                <p className="font-mono text-lg tracking-widest font-semibold">{voucher.code}</p>
              </div>
              <button onClick={copyCode} className="text-xs text-primary hover:underline flex items-center gap-1 mx-auto">
                <Copy size={12} /> Copy code
              </button>
              {message && <p className="text-xs text-muted-foreground italic mt-3">"{message}"</p>}
              <p className="text-[10px] text-muted-foreground mt-3">Redeem online or in-salon</p>
            </Card>

            <div className="flex flex-wrap justify-center gap-3">
              <Link to="/book"><Button className="rounded-xl">Book an appointment</Button></Link>
              <Link to="/"><Button variant="outline" className="rounded-xl">Back to home</Button></Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
